import type { IOperationLogEntry } from '../../../src/models/Operation';

export interface ITreeNode {
  name: string;
  path: string;
  isFolder: boolean;
  /** 'changed': this item moved or was renamed here (after), or is about to (before). 'new': a folder AI Admin created. */
  mark?: 'changed' | 'new' | 'setAside';
  /** Folders: files directly inside, when known. */
  count?: number;
  children: ITreeNode[];
}

export function insert(root: ITreeNode, path: string, isFolder: boolean, mark?: ITreeNode['mark']): ITreeNode {
  const parts: string[] = path.split('/').filter(p => p);
  let node: ITreeNode = root;
  parts.forEach((part, i) => {
    const last: boolean = i === parts.length - 1;
    const childPath: string = parts.slice(0, i + 1).join('/');
    let child: ITreeNode | undefined = node.children.filter(c => c.name.toLowerCase() === part.toLowerCase())[0];
    if (!child) {
      child = { name: part, path: childPath, isFolder: last ? isFolder : true, children: [] };
      node.children.push(child);
    }
    if (last && mark && !child.mark) {
      child.mark = mark;
    }
    node = child;
  });
  return node;
}

export function sort(n: ITreeNode): ITreeNode {
  n.children.sort((a, b) => (a.isFolder === b.isFolder ? a.name.localeCompare(b.name) : a.isFolder ? -1 : 1));
  n.children.forEach(sort);
  return n;
}

const SET_ASIDE: RegExp = /^(set aside by ai admin|set aside|_quarantine)(\/|$)/i;

/**
 * The part of the library a batch touched, before and after: only the items it changed and the
 * folders around them, so the difference is easy to see.
 */
export function beforeAfter(entries: IOperationLogEntry[]): { before: ITreeNode; after: ITreeNode } {
  const before: ITreeNode = { name: '', path: '', isFolder: true, children: [] };
  const after: ITreeNode = { name: '', path: '', isFolder: true, children: [] };
  for (const e of entries) {
    const folder: boolean = e.itemKind === 'folder';
    if (e.action === 'createFolder') {
      if (e.status === 'success') {
        insert(after, e.destinationPath, true, 'new');
      }
      continue;
    }
    if (e.action === 'undo' && !e.newName && folder) {
      // An undo that removed an empty folder AI Admin had created: it was there before, not after.
      insert(before, e.sourcePath, true);
      continue;
    }
    if (!e.sourcePath) {
      continue;
    }
    const done: boolean = e.status === 'success';
    insert(before, e.sourcePath, folder, done ? 'changed' : undefined);
    const to: string = done ? e.destinationPath : e.sourcePath;
    insert(after, to, folder, done ? (SET_ASIDE.test(to) ? 'setAside' : 'changed') : undefined);
  }
  return { before: sort(before), after: sort(after) };
}

export function countNodes(n: ITreeNode): number {
  return n.children.reduce((sum, c) => sum + 1 + countNodes(c), 0);
}
